import Link from '@/components/core/Link';
import { ModalsKey } from '@/enums/modalsKey';
import useAuthForm from '@/hooks/auth/useAuthForm';
import { useAuthFormButtonLabel } from '@/hooks/auth/useAuthFormButtonLabel';
import { useAuthFormState } from '@/hooks/auth/useAuthFormState';
import useRegisterVoucher from '@/hooks/register/useRegisterVoucher';
import { useSystemConfiguration } from '@/hooks/theme/useSystemConfiguration';
import { AuthService } from '@/services/AuthService';
import { RegisterFormProps } from '@/types/register/props';
import {
  Animation,
  Button,
  TransWithDefault,
} from '@starsoft/common/components';
import { SettingLayoutComponentId } from '@starsoft/common/models';
import { OriginId } from '@starsoft/common/models/origin/id';
import useTranslation from 'next-translate/useTranslation';
import { PropsWithChildren } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import OAuthRow from '../../OAuth';
import ReferralVoucherInput from '../Content/ReferralVoucherInput';
import styles from './styles.module.scss';

export default function RegisterLayout({
  children,
  id = 1n,
}: PropsWithChildren<{ id: bigint }>): JSX.Element {
  const isVariant =
    id?.toString() === SettingLayoutComponentId.LoginV3?.toString();
  const { t } = useTranslation('register');
  const formMethods = useForm<RegisterFormProps>();

  const { handleSubmit, watch, setValue } = formMethods;

  const { voucher } = useRegisterVoucher({ setValue });
  const {
    isFormStep,
    step,
    onError,
    onSubmit,
    voucherData,
    useRegisterMutation: { isLoading: isLoadingRegister },
    useReferralVoucherByCodeQuery: {
      isLoading: isLoadingVoucherCode,
      isError: isErrorVoucherCode,
    },
  } = useAuthForm({
    methods: formMethods,
  });
  const formState = useAuthFormState({ methods: formMethods });
  const label = useAuthFormButtonLabel({ methods: formMethods });
  const { originId } = useSystemConfiguration();

  const formVoucher = watch('voucher');

  return (
    <Animation
      type="fadeInLeft"
      key={step}
      className={`${styles.container} ${isVariant ? styles['container--variant'] : ''}`}
    >
      {formVoucher && voucherData && isFormStep && (
        <span className={styles.container__label}>
          {t('sponsor_label')} {/* @ts-ignore */}
          <strong>{voucherData?.user?.player?.username}</strong>
        </span>
      )}
      <FormProvider {...formMethods}>
        <form
          className={styles.container__form}
          //@ts-ignore
          onSubmit={handleSubmit(onSubmit, onError)}
          id="registerForm"
        >
          {children}
          {isFormStep && (
            <>
              <ReferralVoucherInput
                isError={isErrorVoucherCode}
                isLoading={isLoadingVoucherCode}
                hasDefaultVoucher={!!voucher}
                voucherData={voucherData}
              />

              <div className={styles.container__terms}>
                <i className="fa-solid fa-circle-info" />

                <TransWithDefault
                  i18nKey="register:terms_label"
                  components={[
                    <p key={0} />,
                    <Link
                      key={1}
                      target="_blank"
                      href="/term/terms-of-service"
                    />,
                  ]}
                />
              </div>
            </>
          )}

          <Button
            type="submit"
            form="registerForm"
            isloading={isLoadingRegister || AuthService.isAuthorized()}
            disabled={formState || isLoadingVoucherCode}
          >
            {label}
          </Button>
        </form>
      </FormProvider>
      {isFormStep && (
        <>
          <span className={styles.container__binding}>
            {t('register_label_binding')}
          </span>
          {originId !== OriginId.Brazil && (
            <OAuthRow
              direction={isVariant ? 'row' : 'column'}
              voucher={formVoucher}
            />
          )}
          {!isVariant && (
            <span className={styles.container__text}>
              {t('login_text')}
              <Link
                modal={ModalsKey.Login}
                className={styles.container__text__highlight}
              >
                {t('login_label')}
              </Link>
            </span>
          )}
        </>
      )}
    </Animation>
  );
}
